import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Wallet, DollarSign, CreditCard, ArrowUpRight, ArrowDownLeft,
  Clock, Shield, AlertCircle, FileText, ChevronDown, Search,
  Filter, Eye, EyeOff, Lock, Briefcase, Plus, X,
  ArrowRight, RefreshCw, Download, Upload, Loader2
} from "lucide-react";
import { Card } from "../components/ui/card";
import SharedHeader4 from "../Headers/SharedHeader4";
import SharedFooter3 from "../Footer/SharedFooter3";
import { escrowAPI, EscrowStatus, EscrowStatusEnum } from "../services/escrowAPI";
import { useSelector, useDispatch } from "react-redux";
import { RootState, useAppDispatch, useAppSelector } from "../redux/store";
import { useNavigate } from "react-router-dom";
import { PaymentMethodsProvider } from "../contexts/PaymentMethodsContext";
import { WithdrawalProvider } from "../contexts/WithdrawalContext";
import PaymentMethodsManager from "../components/payment/PaymentMethodsManager";
import StripeConnectSetup from "../components/ui/StripeConnectSetup";
import WithdrawalMethodsSetup from "../components/payment/WithdrawalMethodsSetup";
import WithdrawalForm from "../components/payment/WithdrawalForm";
import WithdrawalsList from "../components/payment/WithdrawalsList";
import AddFundsModal from "../components/payment/AddFundsModal";
import ReceiptDialog from "../components/ui/ReceiptDialog";
import ProfileCompletionDialog from "../components/ui/ProfileCompletionDialog";
import VerificationRequirementDialog from "../components/VerificationRequirementDialog";
import { useVerification } from "../hooks/useVerification";
import { RoleEnum, GlobalService } from "../modules";
import { checkUserBillingInfo } from "../utils/billingUtils";
import BillingAddressRequirementDialog from "../components/ui/BillingAddressRequirementDialog";
import { fetchCommissions } from "../redux/Slices/commissionSlice";
import { getApplicableCommissionRule, meetsMinimumPayout, checkBillingCycleAllowsWithdrawal } from "../utils/commissionUtils";
import { CommissionBackendResponse } from "../modules/shared/model";
import { getDynamicCurrencySymbol } from "../lib/currencyUtils";

const WalletDashboard = () => {
  const { t } = useTranslation();
  // Redux state
  const { user } = useSelector((state: RootState) => state.auth);
  const appDispatch = useAppDispatch();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Commission state
  const { items: commissionRules = [] } = useAppSelector((s: any) => s.commissions);
  const [applicableCommissionRule, setApplicableCommissionRule] = useState<CommissionBackendResponse | null>(null);

  // Add scrollbar-hide styles
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .scrollbar-hide {
        -ms-overflow-style: none;
        scrollbar-width: none;
      }
      .scrollbar-hide::-webkit-scrollbar {
        display: none;
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // State management
  const [showBalance, setShowBalance] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showAddFunds, setShowAddFunds] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [activeTab, setActiveTab] = useState("transactions");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showStripeConnect, setShowStripeConnect] = useState(false);
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [selectedTransactionForReceipt, setSelectedTransactionForReceipt] = useState(null);
  const [showProfileCompletionDialog, setShowProfileCompletionDialog] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [showBillingAddressDialog, setShowBillingAddressDialog] = useState(false);
  const [pendingWithdrawal, setPendingWithdrawal] = useState(false);
  const [shouldReopenWithdrawal, setShouldReopenWithdrawal] = useState(false);
  const [pendingAddFunds, setPendingAddFunds] = useState(false);
  
  // Use verification hook
  const { isVerified, goToVerification, verificationData } = useVerification();
  
  // Track if verification data has been loaded at least once
  const [verificationDataLoaded, setVerificationDataLoaded] = useState(false);

  // Wallet data state
  const [walletData, setWalletData] = useState<{
    balance: number;
    escrowHeld: number;
    pendingPayments: number;
    availableBalance: number;
    currency: string;
    transactions: any[];
    totalCount: number;
    hasNextPage: boolean;
  } | null>(null);

  // Check for held funds (calculated after walletData is set)
  const heldFunds = walletData?.transactions?.filter((t: any) => 
    Number(t.status?.id) === 3 && // released
    Number(t.clearingStatus?.id) === 3 // admin_held
  ) || [];
  const totalHeldAmount = heldFunds.reduce((sum: number, t: any) => sum + (t.withdrawlAmount || 0), 0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10
  });

  // Fetch wallet data
  const fetchWalletData = async () => {
    if (!user?.id) {
      setError(t("walletDashboard.userNotAuthenticated"));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await escrowAPI.getUserEscrowBalance(user.id, {
        page: pagination.page,
        limit: pagination.limit,
        filters: {
          escrowStatus: [EscrowStatus.RELEASED, EscrowStatus.WITHDRAWN, EscrowStatus.USER_FUNDED, EscrowStatus.CREDITS_PURCHASED, EscrowStatus.USED_QUOTE_PURCHASED] // Status 3 and 9 as requested
        },
        sort: [{
          orderBy: "createdAt",
          order: "DESC"
        }]
      });

      setWalletData(data);
    } catch (err: any) {
      console.error("Error fetching wallet data:", err);
      setError(err?.response?.data?.message || t("walletDashboard.failedToFetchWalletData"));
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount and when pagination changes
  useEffect(() => {
    fetchWalletData();
  }, [user?.id, pagination.page, pagination.limit]);

  // Fetch commission rules on mount
  useEffect(() => {
    appDispatch(fetchCommissions(undefined));
  }, [appDispatch]);

  // Determine applicable commission rule based on actual released transactions
  useEffect(() => {
    if (commissionRules.length > 0 && user && walletData?.transactions) {
      const userRoleId = Number(user.role?.id);
      
      // Get released transactions (status = 3)
      const releasedTransactions = walletData.transactions.filter(
        (transaction: any) => Number(transaction.status?.id) === 3
      );
      
      console.log("Released transactions for commission check:", releasedTransactions);
      const rule = getApplicableCommissionRule(commissionRules, userRoleId, releasedTransactions);
      console.log("Applied commission rule:", rule);
      setApplicableCommissionRule(rule);
    }
  }, [commissionRules, user, walletData?.transactions]);


  // Helper function for currency formatting
  const formatCurrency = (amount: number) => {
    const formattedAmount = amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${getDynamicCurrencySymbol()}${formattedAmount}`;
  };

  // Helper function to get status display info
  const getStatusInfo = (statusId: number) => {
    switch (statusId) {
      case EscrowStatusEnum.pending:
        return {
          label: t("walletDashboard.statusLabels.pending"),
          color: "text-yellow-600",
          bgColor: "bg-yellow-100",
          badgeClass: "bg-yellow-100 text-yellow-800",
          iconBg: "bg-yellow-100",
          iconColor: "text-yellow-600"
        };
      case EscrowStatusEnum.funded:
        return {
          label: t("walletDashboard.statusLabels.funded"),
          color: "text-blue-600",
          bgColor: "bg-blue-100",
          badgeClass: "bg-blue-100 text-blue-800",
          iconBg: "bg-blue-100",
          iconColor: "text-blue-600"
        };
      case EscrowStatusEnum.released:
        return {
          label: t("walletDashboard.statusLabels.released"),
          color: "text-green-600",
          bgColor: "bg-green-100",
          badgeClass: "bg-green-100 text-green-800",
          iconBg: "bg-green-100",
          iconColor: "text-green-600"
        };
      case EscrowStatusEnum.disputed:
        return {
          label: t("walletDashboard.statusLabels.disputed"),
          color: "text-orange-600",
          bgColor: "bg-orange-100",
          badgeClass: "bg-orange-100 text-orange-800",
          iconBg: "bg-orange-100",
          iconColor: "text-orange-600"
        };
      case EscrowStatusEnum.resolved:
        return {
          label: t("walletDashboard.statusLabels.resolved"),
          color: "text-teal-600",
          bgColor: "bg-teal-100",
          badgeClass: "bg-teal-100 text-teal-800",
          iconBg: "bg-teal-100",
          iconColor: "text-teal-600"
        };
      case EscrowStatusEnum.refunded:
        return {
          label: t("walletDashboard.statusLabels.refunded"),
          color: "text-red-600",
          bgColor: "bg-red-100",
          badgeClass: "bg-red-100 text-red-800",
          iconBg: "bg-red-100",
          iconColor: "text-red-600"
        };
      case EscrowStatusEnum.cancelled:
        return {
          label: t("walletDashboard.statusLabels.cancelled"),
          color: "text-gray-600",
          bgColor: "bg-gray-100",
          badgeClass: "bg-gray-100 text-gray-800",
          iconBg: "bg-gray-100",
          iconColor: "text-gray-600"
        };
      case EscrowStatusEnum.failed:
        return {
          label: t("walletDashboard.statusLabels.failed"),
          color: "text-red-700",
          bgColor: "bg-red-100",
          badgeClass: "bg-red-100 text-red-800",
          iconBg: "bg-red-100",
          iconColor: "text-red-700"
        };
      case EscrowStatusEnum.withdrawn:
        return {
          label: t("walletDashboard.statusLabels.withdrawn"),
          color: "text-purple-600",
          bgColor: "bg-purple-100",
          badgeClass: "bg-purple-100 text-purple-800",
          iconBg: "bg-purple-100",
          iconColor: "text-purple-600"
        };
      case EscrowStatusEnum.request_withdraw:
        return {
          label: t("walletDashboard.statusLabels.withdrawalRequested"),
          color: "text-indigo-600",
          bgColor: "bg-indigo-100",
          badgeClass: "bg-indigo-100 text-indigo-800",
          iconBg: "bg-indigo-100",
          iconColor: "text-indigo-600"
        };
      case EscrowStatusEnum.user_funded:
        return {
          label: t("walletDashboard.statusLabels.fundsAdded"),
          color: "text-emerald-600",
          bgColor: "bg-emerald-100",
          badgeClass: "bg-emerald-100 text-emerald-800",
          iconBg: "bg-emerald-100",
          iconColor: "text-emerald-600"
        };
      case EscrowStatusEnum.credits_purchased:
        return {
          label: t("walletDashboard.statusLabels.creditsPurchased"),
          color: "text-purple-600",
          bgColor: "bg-purple-100",
          badgeClass: "bg-purple-100 text-purple-800",
          iconBg: "bg-purple-100",
          iconColor: "text-purple-600"
        };
      case EscrowStatusEnum.quote_purchased:
        return {
          label: t("walletDashboard.statusLabels.quotePurchased"),
          color: "text-indigo-600",
          bgColor: "bg-indigo-100",
          badgeClass: "bg-indigo-100 text-indigo-800",
          iconBg: "bg-indigo-100",
          iconColor: "text-indigo-600"
        };
      case EscrowStatusEnum.used_credit_purchased:
        return {
          label: t("walletDashboard.statusLabels.creditUsed"),
          color: "text-purple-700",
          bgColor: "bg-purple-100",
          badgeClass: "bg-purple-100 text-purple-800",
          iconBg: "bg-purple-100",
          iconColor: "text-purple-700"
        };
      case EscrowStatusEnum.used_quote_purchased:
        return {
          label: t("walletDashboard.statusLabels.quotePurchased"),
          color: "text-indigo-700",
          bgColor: "bg-indigo-100",
          badgeClass: "bg-indigo-100 text-indigo-800",
          iconBg: "bg-indigo-100",
          iconColor: "text-indigo-700"
        };
      default:
        return {
          label: t("walletDashboard.statusLabels.unknown"),
          color: "text-gray-600",
          bgColor: "bg-gray-100",
          badgeClass: "bg-gray-100 text-gray-800",
          iconBg: "bg-gray-100",
          iconColor: "text-gray-600"
        };
    }
  };

  // Handle filter changes
  const handleFilterChange = (newFilterStatus: string) => {
    setFilterStatus(newFilterStatus);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when filters change
    // Note: For now, we're using fixed status 3 and 9, but you can modify this logic
    // to dynamically change the status filter based on the selected option
  };

  // Handle search
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    // Note: Search functionality would need to be implemented in the backend
    // For now, we'll just update the local state
  };

  // Handle pagination
  const updatePagination = (newPagination: Partial<typeof pagination>) => {
    setPagination(prev => ({ ...prev, ...newPagination }));
  };

  // Refresh wallet data
  const refreshWalletData = () => {
    fetchWalletData();
  };

  // Open receipt dialog for transaction
  const openReceiptDialog = (transaction: any) => {
    if (!transaction) {
      alert(t("walletDashboard.noTransactionDataForReceipt"));
      return;
    }
    setSelectedTransactionForReceipt(transaction);
    setShowReceiptDialog(true);
  };

  // Check if user can proceed with action (profile + verification)
  // Check if user needs to complete profile
  const checkProfileCompletion = () => {
    if (user && !user.phone) {
      setShowProfileCompletionDialog(true);
      return false; // Profile incomplete
    }
    return true; // Profile complete
  };

  // Track when verification data has loaded
  useEffect(() => {
    if (verificationData !== null && verificationData !== undefined) {
      setVerificationDataLoaded(true);
    }
  }, [verificationData]);

  // Check if user needs verification
  const checkVerification = () => {
    // Don't check verification if data hasn't loaded yet
    if (!verificationDataLoaded) {
      return true; // Allow action to proceed while loading
    }
    
    if (!isVerified()) {
      setShowVerificationDialog(true);
      return false; // Verification incomplete
    }
    return true; // Verification complete
  };

  // Check both profile and verification
  const checkRequirements = () => {
    // Don't block actions if verification data is still loading
    if (!verificationDataLoaded) {
      // Only check profile completion while verification loads
      if (!checkProfileCompletion()) {
        return false;
      }
      return true;
    }
    
    // Check verification first (higher priority)
    if (!checkVerification()) {
      return false;
    }
    
    // Then check profile completion
    if (!checkProfileCompletion()) {
      return false;
    }
    
    return true;
  };

  // Helper function to navigate to profile based on user role
  const navigateToProfile = () => {
    if (!user?.role?.id) return;
    
    const roleId = Number(user.role.id);
    if (roleId === RoleEnum.professional || roleId === RoleEnum.internationl_professional) {
      navigate("/myprofile");
    } else if (roleId === RoleEnum.client || roleId === RoleEnum.internationl_client) {
      navigate("/client/profile");
    } else if (roleId === RoleEnum.admin) {
      navigate("/admin/dashboard");
    }
    setShowProfileCompletionDialog(false);
  };

  // Helper function to handle billing saved callback
  const handleBillingSaved = () => {
    // Check if user now has billing information
    const billingCheck = checkUserBillingInfo(user);
    
    // After billing info is saved, check and open appropriate action if pending
    if (billingCheck.hasBillingInfo) {
      if (pendingWithdrawal) {
        setShowWithdraw(true);
        setPendingWithdrawal(false);
      } else if (pendingAddFunds) {
        setShowAddFunds(true);
        setPendingAddFunds(false);
      }
    }
  };

  // Handle navigation to withdrawal methods tab
  const handleNavigateToWithdrawalMethods = () => {
    setActiveTab("withdrawal_methods");
    setShouldReopenWithdrawal(true);
  };

  // Effect to reopen withdrawal form after adding method in withdrawal methods tab
  useEffect(() => {
    if (shouldReopenWithdrawal && activeTab === "withdrawal_methods") {
      // Wait a bit for user to add the method, then check if they've switched back
      // This is handled by the WithdrawalMethodsSetup component's onSuccess callbacks
    }
  }, [shouldReopenWithdrawal, activeTab]);

  // Check if user has Stripe Connect setup
  const hasStripeConnect = (user as any)?.stripeConnectedAccountId;
  const needsStripeSetup = false;

  // Transaction modal component
  const TransactionModal = ({ transaction, onClose }) => {
    if (!transaction) return null;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4"
      >
        <motion.div
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.95 }}
          className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-4 sm:p-6"
        >
          <div className="flex justify-between items-start mb-4 sm:mb-6">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">{t("walletDashboard.transactionDetails")}</h2>
              <p className="text-xs sm:text-sm text-gray-600 truncate">ID: {transaction.id}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0 ml-2"
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6 text-gray-500" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-600">{t("walletDashboard.totalAmount")}</span>
                <span className="text-xl font-bold text-gray-900">
                  {formatCurrency(transaction.totalAmount || transaction.amount || 0)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">{t("walletDashboard.baseAmount")}</span>
                <span className="text-gray-900">
                  {formatCurrency(transaction.withdrawlAmount || 0)}
                </span>
              </div>
              {transaction.emergencyCost && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{t("walletDashboard.emergencyCost")}</span>
                  <span className="text-gray-900">
                    {formatCurrency(transaction.emergencyCost)}
                  </span>
                </div>
              )}
              {transaction.trackerAmount && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{t("walletDashboard.trackerAmount")}</span>
                  <span className="text-gray-900">
                    {formatCurrency(transaction.trackerAmount)}
                  </span>
                </div>
              )}
              {transaction.platformFee && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{t("walletDashboard.platformFee")}</span>
                  <span className="text-gray-900">
                    {formatCurrency(transaction.platformFee)}
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">{t("walletDashboard.type")}</span>
                <span className="font-medium capitalize">{t("walletDashboard.payment")}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t("walletDashboard.status")}</span>
                <span className={`inline-flex px-2 py-1 rounded-md text-xs font-medium ${getStatusInfo(Number(transaction.status?.id) || 0).badgeClass}`}>
                  {getStatusInfo(Number(transaction.status?.id) || 0).label}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t("walletDashboard.created")}</span>
                <span className="font-medium">
                  {new Date(transaction.updatedAt).toLocaleString()}
                </span>
              </div>
              {transaction.expectedReleaseDate && (
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("walletDashboard.expectedRelease")}</span>
                  <span className="font-medium">
                    {new Date(transaction.expectedReleaseDate).toLocaleString()}
                  </span>
                </div>
              )}
              {transaction.actualReleaseDate && (
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("walletDashboard.released")}</span>
                  <span className="font-medium">
                    {new Date(transaction.actualReleaseDate).toLocaleString()}
                  </span>
                </div>
              )}
              {transaction.currency && (
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("walletDashboard.currency")}</span>
                  <span className="font-medium">{transaction.currency}</span>
                </div>
              )}
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">{t("walletDashboard.description")}</h3>
              <p className="text-gray-600">
                {transaction.milestone?.title || transaction.notes || t("walletDashboard.paymentForProjectMilestone")}
              </p>
            </div>

            {(Number(transaction.status?.id) === EscrowStatusEnum.funded || Number(transaction.status?.id) === EscrowStatusEnum.user_funded) && (
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-start gap-3">
                  <Lock className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-blue-600">{t("walletDashboard.fundsHeldInEscrow")}</p>
                    {transaction.expectedReleaseDate && (
                      <p className="text-sm text-blue-500">
                        {t("walletDashboard.willBeReleasedOn", { date: new Date(transaction.expectedReleaseDate).toLocaleDateString() })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {Number(transaction.status?.id) === EscrowStatusEnum.disputed && (
              <div className="p-4 bg-orange-50 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-orange-600">{t("walletDashboard.paymentUnderDispute")}</p>
                    {transaction.disputeDescription && (
                      <p className="text-sm text-orange-500 mt-1">
                        {transaction.disputeDescription}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {Number(transaction.status?.id) === EscrowStatusEnum.request_withdraw && (
              <div className="p-4 bg-indigo-50 rounded-lg">
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-indigo-600">{t("walletDashboard.withdrawalRequestPending")}</p>
                    <p className="text-sm text-indigo-500">
                      {t("walletDashboard.withdrawalRequestPendingDesc")}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 mt-4 sm:mt-6">
              <button 
                onClick={() => openReceiptDialog(transaction)}
                className="px-3 sm:px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                <Download className="h-4 w-4" />
                {t("walletDashboard.downloadReceipt")}
              </button>
              {(Number(transaction.status?.id) === EscrowStatusEnum.funded || Number(transaction.status?.id) === EscrowStatusEnum.user_funded) && (
                <button className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm sm:text-base">
                  {t("walletDashboard.viewProject")}
                </button>
              )}
              {transaction.contract && (
                <button className="px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm sm:text-base">
                  {t("walletDashboard.viewContract")}
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  };



  // Loading state
  if (loading && !walletData) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
        <SharedHeader4 />
        <main className="flex-1 max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8 w-full">
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center gap-3">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              <span className="text-gray-600 text-sm sm:text-base">{t("walletDashboard.loadingWalletData")}</span>
            </div>
          </div>
        </main>
        <footer className="bg-white border-t w-full mt-auto">
          <div className="max-w-7xl mx-auto">
            <SharedFooter3 />
          </div>
        </footer>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
        <SharedHeader4 />
        <main className="flex-1 max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8 w-full">
          <div className="flex items-center justify-center h-64">
            <div className="text-center px-4">
              <AlertCircle className="h-10 w-10 sm:h-12 sm:w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{t("walletDashboard.errorLoadingWallet")}</h2>
              <p className="text-gray-600 mb-4 text-sm sm:text-base">{error}</p>
              <button
                onClick={refreshWalletData}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 mx-auto text-sm sm:text-base"
              >
                <RefreshCw className="h-4 w-4" />
                {t("walletDashboard.tryAgain")}
              </button>
            </div>
          </div>
        </main>
        <footer className="bg-white border-t w-full mt-auto">
          <div className="max-w-7xl mx-auto">
            <SharedFooter3 />
          </div>
        </footer>
      </div>
    );
  }

  return (
    <PaymentMethodsProvider>
      <WithdrawalProvider>
        <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
          <SharedHeader4 />

          <main className="flex-1 max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8 w-full">
            <div className="mb-6 sm:mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t("walletDashboard.title")}</h1>
                  <p className="text-gray-600 mt-1 text-sm sm:text-base">{t("walletDashboard.subtitle")}</p>
                </div>
                <button
                  onClick={refreshWalletData}
                  disabled={loading}
                  className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 text-sm sm:text-base"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                  {t("walletDashboard.refresh")}
                </button>
              </div>
            </div>

            {/* Hold for Review Banner */}
            {heldFunds.length > 0 && (
              <div className="mb-6">
                <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-orange-800">
                        {t("walletDashboard.fundsOnHold")}
                      </h3>
                      <div className="mt-2 text-sm text-orange-700">
                        <p>{t("walletDashboard.fundsOnHoldDesc", { amount: formatCurrency(totalHeldAmount), count: heldFunds.length })}</p>
                      </div>
                      {/* <div className="mt-3">
                        <button
                          onClick={() => setActiveTab("transactions")}
                          className="text-sm font-medium text-orange-800 hover:text-orange-900 underline"
                        >
                          {t("walletDashboard.viewHeldTransactions")}
                        </button>
                      </div> */}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Balance Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
              {/* Total Balance */}
              <Card className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Wallet className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                  </div>
                  <button
                    onClick={() => setShowBalance(!showBalance)}
                    className="text-gray-400 hover:text-gray-500 p-1"
                  >
                    {showBalance ? (
                      <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                    ) : (
                      <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                    )}
                  </button>
                </div>
                <h3 className="text-xs sm:text-sm font-medium text-gray-500">{t("walletDashboard.totalBalance")}</h3>
                <div className="mt-1 sm:mt-2">
                  {showBalance ? (
                    <span className="text-lg sm:text-2xl font-semibold text-gray-900">
                      {formatCurrency(walletData?.balance || 0)}
                    </span>
                  ) : (
                    <span className="text-lg sm:text-2xl font-semibold text-gray-900">••••••</span>
                  )}
                </div>
              </Card>

              {/* Available Balance */}
              <Card className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <span className="text-lg font-bold text-green-600">{getDynamicCurrencySymbol()}</span>
                  </div>
                </div>
                <h3 className="text-xs sm:text-sm font-medium text-gray-500">{t("walletDashboard.availableBalance")}</h3>
                <div className="mt-1 sm:mt-2">
                  {showBalance ? (
                    <span className="text-lg sm:text-2xl font-semibold text-gray-900">
                      {formatCurrency(walletData?.availableBalance || 0)}
                    </span>
                  ) : (
                    <span className="text-lg sm:text-2xl font-semibold text-gray-900">••••••</span>
                  )}
                </div>
              </Card>

              {/* Escrow Balance */}
              <Card className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                  </div>
                </div>
                <h3 className="text-xs sm:text-sm font-medium text-gray-500">{t("walletDashboard.inEscrow")}</h3>
                <div className="mt-1 sm:mt-2">
                  {showBalance ? (
                    <span className="text-lg sm:text-2xl font-semibold text-gray-900">
                      {formatCurrency(walletData?.escrowHeld || 0)}
                    </span>
                  ) : (
                    <span className="text-lg sm:text-2xl font-semibold text-gray-900">••••••</span>
                  )}
                </div>
              </Card>

              {/* Pending Balance */}
              <Card className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600" />
                  </div>
                </div>
                <h3 className="text-xs sm:text-sm font-medium text-gray-500">{t("walletDashboard.pendingPayments")}</h3>
                <div className="mt-1 sm:mt-2">
                  {showBalance ? (
                    <span className="text-lg sm:text-2xl font-semibold text-gray-900">
                      {formatCurrency(walletData?.pendingPayments || 0)}
                    </span>
                  ) : (
                    <span className="text-lg sm:text-2xl font-semibold text-gray-900">••••••</span>
                  )}
                </div>
              </Card>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8">
              <button
                onClick={() => {
                  // Check if user needs to complete profile and verification
                  if (!checkRequirements()) {
                    return;
                  }

                  // Check if user has billing information for adding funds
                  const billingCheck = checkUserBillingInfo(user);
                  
                  if (!billingCheck.hasBillingInfo) {
                    // Show billing address requirement dialog and mark add funds as pending
                    setPendingAddFunds(true);
                    setShowBillingAddressDialog(true);
                    return;
                  }

                  // User has billing info, proceed with adding funds
                  setShowAddFunds(true);
                }}
                className="flex items-center justify-center gap-2 px-4 py-3 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Upload className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="text-sm sm:text-base">{t("walletDashboard.addFunds")}</span>
              </button>
              <button
                onClick={() => {
                  // Check minimum payout requirement first
                  console.log("applicableCommissionRule", applicableCommissionRule);
                  if (applicableCommissionRule && walletData?.availableBalance) {
                    const payoutCheck = meetsMinimumPayout(walletData.availableBalance, applicableCommissionRule);
                    if (!payoutCheck.meets) {
                      const categoryName = applicableCommissionRule.categoryType?.name || "your category";
                      GlobalService.showToast(
                        "warning",
                        t("walletDashboard.minimumPayoutNotMet"),
                        t("walletDashboard.minimumPayoutNotMetDesc", {
                          currencySymbol: getDynamicCurrencySymbol(),
                          minimumPayout: payoutCheck.minimumPayout.toFixed(2),
                          categoryName,
                          shortfall: payoutCheck.shortfall.toFixed(2)
                        })
                      );
                      return;
                    }

                    // Check billing cycle restriction
                    const billingCycleCheck = checkBillingCycleAllowsWithdrawal(
                      applicableCommissionRule.billingCycle,
                      (user as any)?.lastWithdrawalDate,
                      undefined
                    );

                    if (!billingCycleCheck.allowed) {
                      const categoryName = applicableCommissionRule.categoryType?.name || "your category";
                      GlobalService.showToast(
                        "warning",
                        t("walletDashboard.billingCycleRestriction"),
                        billingCycleCheck.reason || t("walletDashboard.billingCycleRestrictionDesc", {
                          categoryName,
                          billingCycle: applicableCommissionRule.billingCycle?.name
                        })
                      );
                      return;
                    }
                  }

                  // Check if user has billing information for withdrawals
                  const billingCheck = checkUserBillingInfo(user);
                  
                  if (!billingCheck.hasBillingInfo) {
                    // Show billing address requirement dialog and mark withdrawal as pending
                    setPendingWithdrawal(true);
                    setShowBillingAddressDialog(true);
                    return;
                  }

                  // User has billing info, proceed with withdrawal
                  setShowWithdraw(true);
                }}
                className="flex items-center justify-center gap-2 px-4 py-3 sm:py-2 border text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Download className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="text-sm sm:text-base">{t("walletDashboard.withdraw")}</span>
              </button>
            </div>

            {/* Main Content Tabs */}
            <Card className="mb-6 sm:mb-8">
              <div className="border-b">
                <div className="flex overflow-x-auto scrollbar-hide px-3 sm:px-6">
                  <div className="flex gap-2 sm:gap-6 min-w-max">
                    {[
                      { id: "transactions", label: t("walletDashboard.tabs.transactions") },
                      { id: "payment_methods", label: t("walletDashboard.tabs.paymentMethods") },
                      { id: "withdrawals", label: t("walletDashboard.tabs.withdrawals") },
                      { id: "withdrawal_methods", label: t("walletDashboard.tabs.withdrawalMethods") },
                      { id: "invoices", label: t("walletDashboard.tabs.invoices") }
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`py-3 sm:py-4 relative whitespace-nowrap text-sm sm:text-base font-medium ${activeTab === tab.id
                          ? "text-blue-600"
                          : "text-gray-600 hover:text-gray-900"
                          }`}
                      >
                        {tab.label}
                        {activeTab === tab.id && (
                          <motion.div
                            layoutId="activeTab"
                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
                          />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {activeTab === "payment_methods" && (
                <div className="p-6">
                  {needsStripeSetup ? (
                    <div className="space-y-6">
                      {/* Stripe Connect Setup Required */}
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                        <div className="flex items-start gap-4">
                          <div className="p-2 bg-yellow-100 rounded-lg">
                            <AlertCircle className="h-6 w-6 text-yellow-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                              {t("walletDashboard.paymentSetupRequired")}
                            </h3>
                            <p className="text-yellow-700 mb-4">
                              {t("walletDashboard.paymentSetupRequiredDesc")}
                            </p>
                            <button
                              onClick={() => setShowStripeConnect(true)}
                              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                            >
                              {t("walletDashboard.setupStripeConnect")}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <PaymentMethodsManager availableBalance={walletData?.availableBalance} />
                  )}
                </div>
              )}

              {activeTab === "withdrawals" && (
                <div className="p-6">
                  <div className="space-y-6">
                    {/* Header with Request Withdrawal Button */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{t("walletDashboard.withdrawalsTitle")}</h3>
                        <p className="text-sm text-gray-600">{t("walletDashboard.withdrawalsSubtitle")}</p>
                      </div>
                      <button
                        onClick={() => {
                          // Check minimum payout requirement first
                          if (applicableCommissionRule && walletData?.availableBalance) {
                            const payoutCheck = meetsMinimumPayout(walletData.availableBalance, applicableCommissionRule);
                            if (!payoutCheck.meets) {
                              const categoryName = applicableCommissionRule.categoryType?.name || "your category";
                              GlobalService.showToast(
                                "warning",
                                t("walletDashboard.minimumPayoutNotMet"),
                                t("walletDashboard.minimumPayoutNotMetDesc", {
                                  currencySymbol: getDynamicCurrencySymbol(),
                                  minimumPayout: payoutCheck.minimumPayout.toFixed(2),
                                  categoryName,
                                  shortfall: payoutCheck.shortfall.toFixed(2)
                                })
                              );
                              return;
                            }

                            // Check billing cycle restriction
                            const billingCycleCheck = checkBillingCycleAllowsWithdrawal(
                              applicableCommissionRule.billingCycle,
                              (user as any)?.lastWithdrawalDate,
                              undefined
                            );

                            if (!billingCycleCheck.allowed) {
                              const categoryName = applicableCommissionRule.categoryType?.name || "your category";
                              GlobalService.showToast(
                                "warning",
                                t("walletDashboard.billingCycleRestriction"),
                                billingCycleCheck.reason || t("walletDashboard.billingCycleRestrictionDesc", {
                                  categoryName,
                                  billingCycle: applicableCommissionRule.billingCycle?.name
                                })
                              );
                              return;
                            }
                          }

                          // Check if user has billing information for withdrawals
                          const billingCheck = checkUserBillingInfo(user);
                          
                          if (!billingCheck.hasBillingInfo) {
                            // Show billing address requirement dialog and mark withdrawal as pending
                            setPendingWithdrawal(true);
                            setShowBillingAddressDialog(true);
                            return;
                          }

                          // User has billing info, proceed with withdrawal
                          setShowWithdraw(true);
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        <Download className="h-4 w-4" />
                        {t("walletDashboard.requestWithdrawal")}
                      </button>
                    </div>

                    {/* Withdrawals List */}
                    <WithdrawalsList />
                  </div>
                </div>
              )}

              {activeTab === "withdrawal_methods" && (
                <div className="p-6">
                  <WithdrawalMethodsSetup
                    onComplete={() => {
                      // Optionally show a success message or update UI state
                      console.log("Withdrawal methods setup completed successfully");
                      // Reopen withdrawal form if user came from there
                      if (shouldReopenWithdrawal) {
                        setTimeout(() => {
                          setShowWithdraw(true);
                          setShouldReopenWithdrawal(false);
                        }, 500);
                      }
                    }}
                    onError={(error) => {
                      console.error("Withdrawal methods setup error:", error);
                    }}
                  />
                </div>
              )}

              {activeTab === "transactions" && (
                <>
                  {/* Filters */}
                  <div className="p-4 sm:p-6 border-b">
                    <div className="flex flex-col gap-3 sm:gap-4">
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
                        <div className="relative flex-1">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                          <input
                            type="text"
                            placeholder={t("walletDashboard.searchTransactions")}
                            value={searchQuery}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="w-full pl-9 sm:pl-10 pr-4 py-2.5 sm:py-2 border rounded-lg text-sm sm:text-base"
                          />
                        </div>
                        <button
                          onClick={() => setShowFilters(!showFilters)}
                          className="flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 border rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <Filter className="h-4 w-4 sm:h-5 sm:w-5" />
                          <span className="text-sm sm:text-base">{t("walletDashboard.filters")}</span>
                        </button>
                      </div>
                    </div>

                    <AnimatePresence>
                      {showFilters && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="mt-4 pt-4 border-t"
                        >
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <select
                              value={filterStatus}
                              onChange={(e) => handleFilterChange(e.target.value)}
                              className="w-full px-4 py-2 border rounded-lg"
                            >
                              <option value="all">{t("walletDashboard.allStatus")}</option>
                              <option value="completed">{t("walletDashboard.completed")}</option>
                              <option value="pending">{t("walletDashboard.pending")}</option>
                              <option value="held">{t("walletDashboard.held")}</option>
                              <option value="disputed">{t("walletDashboard.disputed")}</option>
                              <option value="refunded">{t("walletDashboard.refunded")}</option>
                            </select>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Transactions List */}
                  <div className="divide-y">
                    {loading && !walletData?.transactions?.length ? (
                      <div className="p-4 sm:p-6 text-center">
                        <Loader2 className="h-6 w-6 animate-spin text-blue-600 mx-auto mb-2" />
                        <p className="text-gray-600 text-sm sm:text-base">{t("walletDashboard.loadingTransactions")}</p>
                      </div>
                    ) : walletData?.transactions?.length === 0 ? (
                      <div className="p-4 sm:p-6 text-center">
                        <FileText className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">{t("walletDashboard.noTransactionsFound")}</h3>
                        <p className="text-gray-600 text-sm sm:text-base">{t("walletDashboard.noTransactionsDesc")}</p>
                      </div>
                    ) : (
                      walletData?.transactions?.map((transaction) => {
                        const statusId = Number(transaction.status?.id) || 0;
                        const statusInfo = getStatusInfo(statusId);
                        const isUserFunded = statusId === EscrowStatusEnum.user_funded;
                        const isReleased = statusId === EscrowStatusEnum.released;
                        const isFunded = statusId === EscrowStatusEnum.funded;
                        const isCreditsPurchased = statusId === EscrowStatusEnum.credits_purchased;
                        const isQuotePurchased = statusId === EscrowStatusEnum.quote_purchased;
                        const isUsedCreditPurchased = statusId === EscrowStatusEnum.used_credit_purchased;
                        const isUsedQuotePurchased = statusId === EscrowStatusEnum.used_quote_purchased;
                        const isWithdrawn = statusId === EscrowStatusEnum.withdrawn;
                        const isRefunded = statusId === EscrowStatusEnum.refunded;

                        // Determine amount color and sign
                        let amountColor = "text-gray-600";
                        let amountSign = "+";
                        
                        if (isUserFunded || isReleased) {
                          amountColor = "text-green-600";
                          amountSign = "+";
                        } else if (isFunded) {
                          amountColor = "text-green-500";
                          amountSign = "+";
                        } else if (isCreditsPurchased || isQuotePurchased || isUsedCreditPurchased || isUsedQuotePurchased) {
                          amountColor = "text-red-600";
                          amountSign = "-";
                        } else if (isRefunded) {
                          amountColor = "text-red-600";
                          amountSign = "-";
                        } else if (isWithdrawn) {
                          amountColor = "text-red-600";
                          amountSign = "-";
                        }

                        return (
                        <div
                          key={transaction.id}
                          className="p-4 sm:p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                            <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                              <div className={`p-2 rounded-lg flex-shrink-0 ${statusInfo.iconBg}`}>
                                {isReleased ? (
                                  <ArrowDownLeft className={`h-4 w-4 sm:h-5 sm:w-5 ${statusInfo.iconColor}`} />
                                ) : isRefunded ? (
                                  <ArrowUpRight className={`h-4 w-4 sm:h-5 sm:w-5 ${statusInfo.iconColor}`} />
                                ) : isWithdrawn ? (
                                  <Download className={`h-4 w-4 sm:h-5 sm:w-5 ${statusInfo.iconColor}`} />
                                ) : isUserFunded ? (
                                  <Plus className={`h-4 w-4 sm:h-5 sm:w-5 ${statusInfo.iconColor}`} />
                                ) : (
                                  <Lock className={`h-4 w-4 sm:h-5 sm:w-5 ${statusInfo.iconColor}`} />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 text-sm sm:text-base truncate">
                                  {transaction.milestone?.title || t("walletDashboard.payment")}
                                </p>
                                <p className="text-xs sm:text-sm text-gray-500 truncate">
                                  {transaction.id} • {new Date(transaction.updatedAt).toLocaleDateString()}
                                </p>
                                <span className={`inline-flex px-2 py-0.5 rounded-md text-xs mt-1 ${statusInfo.badgeClass}`}>
                                  {statusInfo.label}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center justify-between sm:flex-col sm:items-end gap-2 sm:gap-1">
                              <p className={`font-medium text-sm sm:text-base ${amountColor}`}>
                                {amountSign}
                                {formatCurrency(transaction.withdrawlAmount || 0)}
                              </p>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openReceiptDialog(transaction);
                                }}
                                className="flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                              >
                                <Download className="h-3 w-3" />
                                <span className="hidden sm:inline">{t("walletDashboard.downloadReceipt")}</span>
                                <span className="sm:hidden">{t("walletDashboard.receipt")}</span>
                              </button>
                            </div>
                          </div>
                        </div>
                        );
                      })
                    )}
                  </div>

                  {/* Pagination */}
                  {walletData?.transactions?.length > 0 && (
                      <div className="p-4 sm:p-6 border-t">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
                          <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                            {t("walletDashboard.showingTransactions", { count: walletData.transactions.length, total: walletData.totalCount })}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updatePagination({ page: Math.max(1, pagination.page - 1) })}
                              disabled={pagination.page === 1 || loading}
                              className="px-3 py-2 text-xs sm:text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {t("walletDashboard.previous")}
                            </button>
                            <span className="px-3 py-2 text-xs sm:text-sm text-gray-600">
                              {t("walletDashboard.page", { page: pagination.page })}
                            </span>
                            <button
                              onClick={() => updatePagination({ page: pagination.page + 1 })}
                              disabled={!walletData.hasNextPage || loading}
                              className="px-3 py-2 text-xs sm:text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {t("walletDashboard.next")}
                            </button>
                          </div>
                        </div>
                      </div>
                  )}
                </>
              )}
            </Card>

            {/* Modals */}
            <AnimatePresence>
              {selectedTransaction && (
                <TransactionModal
                  transaction={selectedTransaction}
                  onClose={() => setSelectedTransaction(null)}
                />
              )}
            </AnimatePresence>

            <AddFundsModal
              isOpen={showAddFunds}
              onClose={() => setShowAddFunds(false)}
              onSuccess={() => {
                setShowAddFunds(false);
                refreshWalletData(); // Refresh wallet data after successful funding
              }}
            />

            <AnimatePresence>
              {showWithdraw && (
                <WithdrawalForm
                  onClose={() => setShowWithdraw(false)}
                  availableBalance={walletData?.availableBalance}
                  onNavigateToWithdrawalMethods={handleNavigateToWithdrawalMethods}
                />
              )}
            </AnimatePresence>

            <AnimatePresence>
              {showStripeConnect && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4"
                >
                  <motion.div
                    initial={{ scale: 0.95 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0.95 }}
                    className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                  >
                    <div className="p-4 sm:p-6">
                      <div className="flex justify-between items-center mb-4 sm:mb-6">
                        <h2 className="text-lg sm:text-xl font-bold text-gray-900">Set up Stripe Connect</h2>
                        <button
                          onClick={() => setShowStripeConnect(false)}
                          className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0"
                        >
                          <X className="h-5 w-5 sm:h-6 sm:w-6 text-gray-500" />
                        </button>
                      </div>
                      <StripeConnectSetup
                        onComplete={() => {
                          setShowStripeConnect(false);
                          // Optionally show a success message
                          console.log("Stripe Connect setup completed successfully");
                        }}
                        onError={(error) => {
                          console.error("Stripe Connect setup error:", error);
                        }}
                      />
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Receipt Dialog */}
            <ReceiptDialog
              isOpen={showReceiptDialog}
              onClose={() => {
                setShowReceiptDialog(false);
                setSelectedTransactionForReceipt(null);
              }}
              transaction={selectedTransactionForReceipt}
            />

            {/* Profile and Verification Dialog */}
            {/* Profile Completion Dialog */}
            <ProfileCompletionDialog
              isOpen={showProfileCompletionDialog}
              onClose={() => setShowProfileCompletionDialog(false)}
              onGoToProfile={navigateToProfile}
              userRole={Number(user?.role?.id)}
            />

            {/* Verification Requirement Dialog */}
            <VerificationRequirementDialog
              isOpen={showVerificationDialog}
              onClose={() => setShowVerificationDialog(false)}
              title="Verification Required"
              message="Please complete your account verification to add funds to your wallet."
            />

            {/* Billing Address Requirement Dialog */}
            <BillingAddressRequirementDialog
              isOpen={showBillingAddressDialog}
              onClose={() => {
                setShowBillingAddressDialog(false);
                setPendingWithdrawal(false);
                setPendingAddFunds(false);
              }}
              onBillingSaved={handleBillingSaved}
              userRole={Number(user?.role?.id)}
              action={pendingWithdrawal ? "withdrawal" : "payment"}
            />

          </main>

          {/* Footer Actions - Mobile Only */}
          <footer className="bg-white border-t w-full mt-auto">
          <div className="max-w-7xl mx-auto">
            <SharedFooter3 />
          </div>
        </footer>
        </div>
      </WithdrawalProvider>
    </PaymentMethodsProvider>
  );
};

export default WalletDashboard;